import { useEffect, useRef, useState } from 'react';
import { createBrowserStt, createBrowserTts, ElliottSttEngine, ElliottTtsEngine } from './voiceEngine';
import { createServerStt, createServerTts, isServerVoiceDown, markServerVoiceDown } from './serverVoiceEngine';

// Half-duplex voice conversation state machine for the Live Scenario:
// Elliott speaks a full turn → the mic opens → the learner speaks, and their
// words land in the answer field phrase by phrase → the learner presses Send
// answer → the field is submitted → Elliott replies.
// The mic is ALWAYS closed while Elliott speaks — no barge-in.
//
// A silence NEVER submits. Attempts are limited, so an auto-submit on silence
// can spend one on a half-finished thought; ending the turn is always the
// learner's deliberate click.
//
// The learner's side of a turn is made of RECORDINGS, and each recording of
// PHRASES. A phrase is written into the field as soon as the learner reaches
// a natural break, so they can read and edit their answer as they go. Pausing,
// muting, the idle guard and Replay each end the current recording without
// submitting it: what was said still lands in the field, the microphone is
// released, and a fresh recording starts when the learner carries on.
//
// The learner answers by voice ('speak') or by typing ('type'). Both use the
// same field and the same Send answer; in type mode the mic simply never opens.
// Elliott keeps speaking either way.
//
// Engines cascade per turn: server (OpenAI voice + transcription) → browser
// (Web Speech) → text sim. A failed server round-trip latches the session
// onto the browser engines; a dead browser backend drops to text.
//
// 'preparing' = a spoken turn was requested but no audio is audible yet
//               (TTS round-trip) — the UI shows a loading orb, never a moving mouth.
// 'thinking'  = Send answer was pressed: covers the last phrase being written
//               down AND the evaluation, which read as one wait to the learner.
// 'paused'    = the learner paused, either Elliott mid-sentence or their own mic.
// 'muted'     = the learner muted the mic with its button.
// 'micOff'    = the idle guard closed the mic after a long silence. It stays
//               that way until the learner resumes.
// 'typing'    = the learner's turn, in type mode.
export type VoiceUiState =
  | 'idle'
  | 'preparing'
  | 'speaking'
  | 'listening'
  | 'thinking'
  | 'paused'
  | 'muted'
  | 'micOff'
  | 'typing';

export type VoiceInputMode = 'speak' | 'type';

/** Silence this long while listening and the mic closes itself. */
export const MIC_IDLE_MS = 120_000;
const IDLE_CHECK_MS = 5_000;

/** One stretch of the learner's answer, between a mic opening and closing. */
interface Recording {
  /** Phrases written down and not yet handed to the field. */
  parts: string[];
  /** The engine has handed back everything it heard. */
  done: boolean;
  /** Stopped, and waiting on its last phrases. */
  stopped: boolean;
  /** Real speech was heard, so there may be words still to come. */
  heard: boolean;
}

interface SpeakOptions {
  onStart?: () => void;
  onSpoken?: () => void;
}

interface UseVoiceConversationOptions {
  /** Voice mode on/off. Flipping to false cancels all speech and capture. */
  active: boolean;
  /** A phrase the learner said, in order, for the answer field. */
  onTranscript: (text: string) => void;
  /**
   * Send answer, once everything said has reached the field. The page sends
   * the field and returns true, or returns false when there is nothing to send
   * (the mic then reopens).
   */
  onReadyToSend: () => boolean;
  /** Mic permission denied — the page should fall back to the text sim. */
  onMicDenied: () => void;
  /** Conversational context for the server transcription (Elliott's last turn). */
  getSttContext?: () => string;
}

export function useVoiceConversation({
  active,
  onTranscript,
  onReadyToSend,
  onMicDenied,
  getSttContext
}: UseVoiceConversationOptions) {
  const [state, setStateRaw] = useState<VoiceUiState>('idle');
  const [inputMode, setInputModeRaw] = useState<VoiceInputMode>('speak');
  /** The words of the phrase in progress (browser engine only). */
  const [partial, setPartial] = useState('');
  /** The recording that is open right now has heard real speech. */
  const [heardSpeech, setHeardSpeech] = useState(false);
  /** The open recording holds speech that is not in the field yet. */
  const [hearing, setHearing] = useState(false);
  /** A closed recording is still being written down. */
  const [transcribing, setTranscribing] = useState(false);
  /** What was playing or listening when the learner paused. */
  const [pausedDuring, setPausedDuring] = useState<'speaking' | 'listening' | null>(null);
  // Word-synced caption of Elliott's CURRENT sentence: updates at word rate
  // (~4Hz), so plain state is fine.
  const [caption, setCaption] = useState<{ words: string[]; active: number } | null>(null);
  // Live speech amplitude 0..1 — mutated per frame by the TTS meter and read
  // per frame by the talking avatar; deliberately NOT React state (60fps).
  const speechLevelRef = useRef(0);

  const stateRef = useRef<VoiceUiState>('idle');
  const setState = (next: VoiceUiState) => {
    stateRef.current = next;
    setStateRaw(next);
  };
  const inputModeRef = useRef<VoiceInputMode>('speak');

  const ttsRef = useRef<ElliottTtsEngine | null>(null);
  const sttRef = useRef<ElliottSttEngine | null>(null);
  const activeRef = useRef(active);
  // Set once the page is gone. An answer that was still being evaluated when
  // the learner left comes back afterwards and asks Elliott to speak — with no
  // page left to stop her. Nothing may start after this.
  const leftRef = useRef(false);
  activeRef.current = active && !leftRef.current;
  // Engine callbacks outlive the render they were created in — route them
  // through a ref so they always see the latest handlers.
  const handlersRef = useRef({ onTranscript, onReadyToSend, onMicDenied, getSttContext });
  handlersRef.current = { onTranscript, onReadyToSend, onMicDenied, getSttContext };

  // Continuation of the turn Elliott is currently speaking — held so Skip can
  // cut the audio and jump straight to it (normally onEnd fires this).
  const speakDoneRef = useRef<(() => void) | null>(null);
  const speakStartedRef = useRef(false);
  const ttsPausedRef = useRef(false);

  const recordingsRef = useRef<Recording[]>([]);
  const openRecordingRef = useRef<Recording | null>(null);
  const submitPendingRef = useRef(false);
  const pausedDuringRef = useRef<'speaking' | 'listening' | null>(null);
  const setPaused = (during: 'speaking' | 'listening' | null) => {
    pausedDuringRef.current = during;
    setPausedDuring(during);
  };

  const lastActivityRef = useRef(0);
  const idleTimerRef = useRef<number | null>(null);

  const clearIdle = () => {
    if (idleTimerRef.current !== null) {
      window.clearInterval(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const resetTurn = () => {
    recordingsRef.current = [];
    openRecordingRef.current = null;
    submitPendingRef.current = false;
    setTranscribing(false);
  };

  const stopAll = () => {
    ttsRef.current?.cancel();
    sttRef.current?.abort();
    sttRef.current = null;
    speakDoneRef.current = null;
    speechLevelRef.current = 0;
    ttsPausedRef.current = false;
    clearIdle();
    resetTurn();
    setPaused(null);
    setCaption(null);
    setPartial('');
    setHeardSpeech(false);
    setHearing(false);
    setState('idle');
  };
  const stopAllRef = useRef(stopAll);
  stopAllRef.current = stopAll;

  useEffect(() => {
    if (!active) stopAllRef.current();
  }, [active]);
  useEffect(() => {
    leftRef.current = false;
    activeRef.current = active;
    return () => {
      leftRef.current = true;
      activeRef.current = false;
      stopAllRef.current();
    };
    // Mount / unmount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every phrase lands here and goes to the field in the order it was spoken,
  // even when a later recording is written down before an earlier one.
  // Written as a ref so engine callbacks from earlier renders call the latest.
  const settleRef = useRef<() => void>(() => undefined);
  const listenRef = useRef<() => void>(() => undefined);

  settleRef.current = () => {
    const recs = recordingsRef.current;
    while (recs.length) {
      const [head] = recs;
      while (head.parts.length) {
        const text = head.parts.shift() as string;
        if (text) handlersRef.current.onTranscript(text);
      }
      if (!head.done) break;
      recs.shift();
    }
    const waiting = recs.some((r) => r.stopped && !r.done);
    // Only a recording that heard speech has words on the way. A silent one
    // still has to close before anything later is handed over, but showing
    // it as "writing" would flash Send answer on for nothing.
    setTranscribing(recs.some((r) => r.stopped && !r.done && r.heard));
    if (waiting || !activeRef.current || !submitPendingRef.current) return;

    submitPendingRef.current = false;
    resetTurn();
    if (!handlersRef.current.onReadyToSend()) {
      // Nothing to send after all (a cough, or the transcription was lost):
      // back to the learner's turn.
      window.setTimeout(() => {
        if (activeRef.current) listenRef.current();
      }, 0);
    }
  };

  /** End the open recording WITHOUT submitting it: its words still reach the field. */
  const closeRecording = () => {
    const engine = sttRef.current;
    const rec = openRecordingRef.current;
    sttRef.current = null;
    openRecordingRef.current = null;
    clearIdle();
    setPartial('');
    setHeardSpeech(false);
    setHearing(false);
    if (!engine || !rec) return;
    rec.stopped = true;
    if (rec.heard) setTranscribing(true);
    engine.stop();
  };

  const armIdle = () => {
    clearIdle();
    idleTimerRef.current = window.setInterval(() => {
      if (stateRef.current !== 'listening' || !sttRef.current) return;
      if (Date.now() - lastActivityRef.current < MIC_IDLE_MS) return;
      // Walked away, or thinking for a long time. Either way, stop recording
      // the room, and keep anything already said.
      closeRecording();
      setState('micOff');
      settleRef.current();
    }, IDLE_CHECK_MS);
  };

  /** The learner's turn: the mic opens, or in type mode the field waits. */
  const listen = () => {
    if (!activeRef.current) return;
    ttsRef.current?.cancel();
    setPaused(null);
    setCaption(null);
    setPartial('');
    setHeardSpeech(false);
    setHearing(false);
    if (inputModeRef.current === 'type') {
      closeRecording();
      setState('typing');
      return;
    }
    const serverEngine = !isServerVoiceDown()
      ? createServerStt(() => handlersRef.current.getSttContext?.() || '')
      : null;
    const engine = serverEngine || createBrowserStt();
    sttRef.current?.abort();
    sttRef.current = engine;
    if (!engine) return;
    setState('listening');

    const rec: Recording = { parts: [], done: false, stopped: false, heard: false };
    recordingsRef.current.push(rec);
    openRecordingRef.current = rec;
    const dropRecording = () => {
      recordingsRef.current = recordingsRef.current.filter((r) => r !== rec);
      if (openRecordingRef.current === rec) openRecordingRef.current = null;
    };
    const isOpen = () => sttRef.current === engine;
    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };
    markActivity();
    armIdle();

    engine.start({
      onPartial: (text) => {
        if (isOpen()) setPartial(text);
      },
      onSpeechStart: () => {
        markActivity();
        rec.heard = true;
        if (!isOpen()) return;
        setHeardSpeech(true);
        setHearing(true);
      },
      onActivity: markActivity,
      onPending: (pending) => {
        if (isOpen()) setHearing(pending);
      },
      onPhrase: (text) => {
        rec.heard = true;
        rec.parts.push(text.trim());
        settleRef.current();
      },
      onFinal: (text) => {
        // The only way here is our own stop(): Send answer, mute, Pause,
        // Replay, type mode or the idle guard.
        if (text.trim()) rec.parts.push(text.trim());
        rec.done = true;
        rec.stopped = true;
        if (openRecordingRef.current === rec) openRecordingRef.current = null;
        settleRef.current();
      },
      onPermissionDenied: () => {
        dropRecording();
        clearIdle();
        setPartial('');
        setState('idle');
        handlersRef.current.onMicDenied();
      },
      onUnavailable: () => {
        dropRecording();
        clearIdle();
        setPartial('');
        if (engine === serverEngine) {
          // Server capture unusable — retry the turn on the browser engine.
          markServerVoiceDown();
          window.setTimeout(() => {
            if (activeRef.current) listenRef.current();
          }, 0);
          return;
        }
        // Unusable browser backend (Chromium-family) — same fallback as
        // denial: the page drops to the text sim with a subtle note.
        setState('idle');
        handlersRef.current.onMicDenied();
      }
    });
  };
  listenRef.current = listen;

  const speak = (text: string, opts?: SpeakOptions) => {
    if (!activeRef.current) return;
    // A recording is running when Elliott is asked to speak (Replay mid-answer).
    // What the learner has said so far still lands in the field; the mic opens
    // again once she is done.
    const hadCapture = Boolean(sttRef.current);
    if (hadCapture) closeRecording();
    setPaused(null);
    ttsPausedRef.current = false;
    speakStartedRef.current = false;
    // Loading until audio is actually audible — 'speaking' flips on onStart.
    setState('preparing');
    // Runs exactly once per turn: natural end, TTS failure, or Skip. TTS
    // failure falls through to the same continuation — the turn's text is on
    // screen either way, so the conversation must not stall.
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      speakDoneRef.current = null;
      speechLevelRef.current = 0;
      setCaption(null);
      if (!activeRef.current) return;
      setState('idle');
      opts?.onSpoken?.();
    };
    speakDoneRef.current = done;
    // onStart fires once, whichever engine in the cascade actually voices.
    let started = false;
    const handleStart = () => {
      if (started || !activeRef.current) return;
      started = true;
      speakStartedRef.current = true;
      if (!ttsPausedRef.current) setState('speaking');
      opts?.onStart?.();
    };
    // Server voice first, retrying the SAME turn on the browser voice so no
    // turn ever goes silently unspoken — but only the part the server did not
    // voice, so the learner never hears the opening lines twice.
    ttsRef.current?.cancel();
    const speakWith = (engine: ElliottTtsEngine, next: ElliottTtsEngine | null, textToSpeak: string) => {
      ttsRef.current = engine;
      engine.speak(textToSpeak, {
        onStart: handleStart,
        onLevel: (level) => {
          speechLevelRef.current = level;
        },
        onCaption: (words, activeIndex) => {
          if (activeRef.current) setCaption({ words, active: activeIndex });
        },
        onEnd: done,
        onError: (error) => {
          const rest = (error?.remaining ?? textToSpeak).trim();
          if (next && activeRef.current && rest) speakWith(next, null, rest);
          else done();
        }
      });
      // The learner paused before this engine took over: it starts held too.
      if (ttsPausedRef.current) engine.pause();
    };
    const begin = () => {
      // Skip / cancel / mode change during the hand-off — nothing left to say.
      if (settled || !activeRef.current) return;
      const serverTts = !isServerVoiceDown() ? createServerTts() : null;
      if (serverTts) speakWith(serverTts, createBrowserTts(), text);
      else speakWith(createBrowserTts(), null, text);
    };
    // Aborting SpeechRecognition releases the mic ASYNCHRONOUSLY, and Chrome
    // silently swallows a speechSynthesis utterance started while that
    // teardown is still in flight. Let the mic finish releasing first.
    if (hadCapture) window.setTimeout(begin, 250);
    else begin();
  };

  /**
   * Send answer. Feedback is immediate; the field is sent once the last
   * phrase has reached it, so nothing said at the very end is left out.
   */
  const send = () => {
    const { current } = stateRef;
    if (submitPendingRef.current) return;
    if (current === 'speaking' || current === 'preparing' || current === 'thinking') return;
    if (current === 'paused' && pausedDuringRef.current === 'speaking') return;
    submitPendingRef.current = true;
    setPaused(null);
    // Before the last phrase is even written down, so the button can never
    // look dead while the transcription is working.
    setState('thinking');
    closeRecording();
    settleRef.current();
  };

  /** Speak answer / Type answer. Elliott keeps her voice in both. */
  const setInputMode = (mode: VoiceInputMode) => {
    if (mode === inputModeRef.current) return;
    inputModeRef.current = mode;
    setInputModeRaw(mode);
    const { current } = stateRef;
    const learnersTurn =
      current === 'listening' ||
      current === 'muted' ||
      current === 'micOff' ||
      current === 'typing' ||
      (current === 'paused' && pausedDuringRef.current === 'listening');
    // While Elliott speaks or considers, the new mode applies when the turn
    // comes back to the learner.
    if (!learnersTurn) return;
    if (mode === 'type') {
      closeRecording();
      setPaused(null);
      setState('typing');
      settleRef.current();
    } else {
      listen();
    }
  };

  /** The mic button: mute an open mic, or open a closed one. */
  const toggleMic = () => {
    const { current } = stateRef;
    if (current === 'listening') {
      closeRecording();
      setState('muted');
      settleRef.current();
      return;
    }
    if (inputModeRef.current === 'type') {
      setInputMode('speak');
      return;
    }
    if (
      current === 'muted' ||
      current === 'micOff' ||
      (current === 'paused' && pausedDuringRef.current === 'listening')
    ) {
      listen();
    }
  };

  /** Pause whatever is happening: Elliott mid-sentence, or the learner's mic. */
  const pause = () => {
    const { current } = stateRef;
    if (current === 'speaking' || current === 'preparing') {
      ttsPausedRef.current = true;
      ttsRef.current?.pause();
      speechLevelRef.current = 0;
      setPaused('speaking');
      setState('paused');
    } else if (current === 'listening') {
      closeRecording();
      setPaused('listening');
      setState('paused');
      settleRef.current();
    }
  };

  /** Carry on from a pause, or reopen the mic after the idle guard closed it. */
  const resume = () => {
    const { current } = stateRef;
    if (current !== 'paused' && current !== 'micOff') return;
    if (current === 'paused' && pausedDuringRef.current === 'speaking') {
      ttsPausedRef.current = false;
      setPaused(null);
      setState(speakStartedRef.current ? 'speaking' : 'preparing');
      ttsRef.current?.resume();
      return;
    }
    setPaused(null);
    listen();
  };

  /**
   * "Skip" while Elliott is speaking — cut the audio short and run the turn's
   * continuation immediately (usually: open the mic).
   */
  const skipSpeaking = () => {
    const finish = speakDoneRef.current;
    if (!finish) return;
    ttsRef.current?.cancel();
    ttsPausedRef.current = false;
    finish();
  };

  return {
    state,
    inputMode,
    pausedDuring,
    partial,
    heardSpeech,
    hearing,
    transcribing,
    caption,
    speechLevelRef,
    speak,
    listen,
    send,
    toggleMic,
    setInputMode,
    pause,
    resume,
    skipSpeaking,
    stopAll
  };
}
